import React, { useMemo, useState } from 'react'
import { css, Global } from '@emotion/core'
import { Option, Select } from '@/components/atoms/select'

export type FixedLabelMultiSelectOption = {
  value: string | number
  label: string
  disabled?: boolean
}

type Props = {
  label: string
  value: string[] | number[]
  options: FixedLabelMultiSelectOption[]
  onChange: (value: string[] | number[]) => void
  disabled?: boolean
  width?: number
  dropdownWidth?: number
  notFoundContent?: React.ReactNode
}

export const FixedLabelMultiSelect: React.FC<Props> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  width = 116,
  dropdownWidth,
  notFoundContent,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const isActive = value.length > 0

  const selectCustomStyle = useMemo(
    () =>
      css({
        paddingBottom: 0,
        width,
        '.ant-select': {
          width: '100%',
        },
        '.ant-select-selection': {
          minHeight: 36,
          borderRadius: 18,
          borderColor: isActive || isOpen ? '#F8AA00 !important' : '#CCCCCC !important',
          boxShadow: 'none !important',
        },
        '.ant-select-selection--multiple': {
          paddingBottom: 0,
        },
        '.ant-select-selection__rendered': {
          minHeight: 34,
          marginLeft: '12px !important',
          marginRight: '32px !important',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          overflow: 'hidden',
        },
        '.ant-select-selection__choice': {
          display: 'none',
        },
        '.ant-select-search--inline': {
          width: '100%',
          opacity: 0,
        },
        '.ant-select-search__field': {
          color: '#272727',
          fontSize: 14,
        },
        '.ant-select-arrow': {
          opacity: 0,
        },
      }),
    [isActive, isOpen, width],
  )

  return (
    <>
      <Global styles={dropdownPopupStyle} />
      <div css={containerStyle}>
        <Select
          mode="multiple"
          showSearch
          value={value}
          disabled={disabled}
          onChange={nextValue => onChange(nextValue as string[] | number[])}
          onDropdownVisibleChange={visible => setIsOpen(visible)}
          optionFilterProp="children"
          notFoundContent={notFoundContent}
          dropdownClassName="fixed-label-multi-select-dropdown"
          dropdownStyle={dropdownWidth ? { width: dropdownWidth, minWidth: dropdownWidth } : undefined}
          menuItemSelectedIcon={
            <img className="fixed-label-multi-select-check" src={require('@/static/images/customer-manager/icon_check.svg')} alt="" />
          }
          customStyle={selectCustomStyle}
        >
          {options.map(option => (
            <Option key={String(option.value)} value={option.value} disabled={option.disabled}>
              {option.label}
            </Option>
          ))}
        </Select>

        <div css={overlayStyle(isActive, disabled)}>
          <span>{label}</span>
        </div>

        <img css={arrowStyle} src={require('@/static/images/arrow_down_yellow.svg')} alt="" />

        {value.length > 0 && !disabled && <div css={badgeStyle}>{value.length}</div>}
      </div>
    </>
  )
}

const dropdownPopupStyle = css({
  '.fixed-label-multi-select-dropdown': {
    borderRadius: 8,
    boxShadow: '0 3px 10px #0000003d',
  },
  '.fixed-label-multi-select-dropdown .ant-select-dropdown-menu': {
    maxHeight: 320,
    padding: '0 !important',
  },
  '.fixed-label-multi-select-dropdown .ant-select-dropdown-menu-item': {
    minHeight: 44,
    padding: '10px 40px 10px 16px',
    fontSize: 14,
    lineHeight: '24px',
    color: '#272727',
    whiteSpace: 'nowrap',
  },
  '.fixed-label-multi-select-dropdown .ant-select-dropdown-menu-item-selected': {
    background: '#fffbe6',
    color: '#F8AA00 !important' as any,
    fontWeight: 'bold !important' as any,
  },
  '.fixed-label-multi-select-dropdown .ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled)': {
    background: '#f2f2f2',
  },
  '.fixed-label-multi-select-dropdown .fixed-label-multi-select-check': {
    width: 12,
    height: 12,
  },
})

const containerStyle = css({
  position: 'relative',
})

const overlayStyle = (isActive: boolean, disabled: boolean) =>
  css({
    position: 'absolute',
    left: 16,
    right: 36,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    color: disabled ? '#BFBFBF' : isActive ? '#F8AA00' : '#676767',
    fontSize: 14,
    fontWeight: isActive ? 'bold' : 'normal',
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
  })

const arrowStyle = css({
  position: 'absolute',
  right: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 12,
  height: 7,
  pointerEvents: 'none',
})

const badgeStyle = css({
  position: 'absolute',
  top: -6,
  right: -6,
  minWidth: 24,
  height: 24,
  padding: '0 4px',
  borderRadius: 20,
  backgroundColor: '#F8AA00',
  color: '#FFFFFF',
  fontSize: 11,
  fontWeight: 'bold',
  lineHeight: '24px',
  textAlign: 'center',
  pointerEvents: 'none',
  letterSpacing: 0,
})
